import React, { useRef, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import useSWR from 'swr';
import {
  Form,
  Row,
  Col,
  Table,
  Button,
  Card,
  Spinner,
  Alert,
  InputGroup,
} from 'react-bootstrap';
import InputMask from 'react-input-mask';
import { FaWhatsapp } from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../../services/api';
import selectErgoById from '../../../services/ergo';
import history from '../../../services/history';
import LaudoCardHeader from '../../../components/laudos/LaudoCardHeader';
import generateErgoPDF from './generateErgoPDF';

const DeleteButton = ({ field, onDelete }) => (
  <Button
    variant="outline-danger"
    size="sm"
    style={{ marginLeft: 4, padding: '0 6px', height: 30 }}
    onClick={() => onDelete(field)}
    tabIndex={-1}
  >
    ×
  </Button>
);

export default function FormErgo() {
  const { id } = useParams();
  const { data: ergo, error, isLoading, mutate } = useSWR(
    id ? ['ergo', id] : null,
    () => selectErgoById(id)
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pageBreakA, setPageBreakA] = useState(false);
  const [pageBreakB, setPageBreakB] = useState(false);
  const [pageBreakC, setPageBreakC] = useState(false);
  const [formData, setFormData] = useState({
    codErgo: '',
    indicacoes: Array(5).fill(''),
    medicacoes: Array(10).fill(''),
    fatoresRisco: Array(5).fill(''),
    queixas: Array(5).fill(''),
    atividadeFisica: Array(5).fill(''),
    ecgBasal: Array(5).fill(''),
    exameFisico: 'RCR,  2T S/ SOPRO.  MV (+),  BILATERAL S/ RA.',
    fcMax: '',
    fcSub: '',
    fcDeitado: '',
    paDeitado: '',
    fcEmPe: '',
    paEmPe: '',
    protocolo: '',
    estagios: Array.from({ length: 8 }, (_, i) => ({
      tempo: '',
      vel: '',
      inc: '',
      fc: '',
      pa: '',
      sintomas: '',
      arritmias: '',
      id: i + 1,
    })),
    recuperacao: Array.from({ length: 4 }, (_, i) => ({
      tempo: '2',
      fc: '',
      pa: '',
      sintomas: '',
      arritmias: '',
      id: i + 1,
    })),
    alteracoesECG: Array(5).fill(''),
    arritmias: Array(5).fill(''),
    comportamentoPA: Array(5).fill(''),
    comentario: Array(5).fill(''),
    conclusao: Array(5).fill(''),
  });

  // Carrega os dados do state ergo ao montar ou quando mudar
  useEffect(() => {
    if (ergo) {
      const proto = ergo.PROTOCOLO || '';
      const maxEstagios = proto === 'Rampa' ? 12 : 8;

      setFormData(prev => ({
        ...prev,
        codErgo: ergo.CODEXAME || '',
        exameFisico: ergo.EXAMEFISICO || '',
        fcMax: ergo.FREQMAX || '',
        fcSub: ergo.FREQSUB || '',
        fcDeitado: ergo.FCDEITADO || '',
        paDeitado: ergo.PADEITADO || '',
        fcEmPe: ergo.FCPE || '',
        paEmPe: ergo.PAPE || '',
        protocolo: proto,
        indicacoes: Array.isArray(ergo.indicacoes)
          ? ergo.indicacoes
              .map(i => i.CODINDICACAO)
              .concat(Array(5).fill(''))
              .slice(0, 5)
          : Array(5).fill(''),
        medicacoes: Array.isArray(ergo.medicamentos)
          ? ergo.medicamentos
              .map(i => i.CODMEDICTO)
              .concat(Array(10).fill(''))
              .slice(0, 10)
          : Array(10).fill(''),
        fatoresRisco: Array.isArray(ergo.fatoresRisco)
          ? ergo.fatoresRisco
              .map(i => i.CODFATRISCO)
              .concat(Array(5).fill(''))
              .slice(0, 5)
          : Array(5).fill(''),
        queixas: Array.isArray(ergo.queixas)
          ? ergo.queixas
              .map(i => i.CODQUEIXA)
              .concat(Array(5).fill(''))
              .slice(0, 5)
          : Array(5).fill(''),
        atividadeFisica: Array.isArray(ergo.atividadesFisica)
          ? ergo.atividadesFisica
              .map(i => i.CODATVFISICA)
              .concat(Array(5).fill(''))
              .slice(0, 5)
          : Array(5).fill(''),
        ecgBasal: Array.isArray(ergo.ecgBasal)
          ? ergo.ecgBasal
              .map(i => i.CODECGBASAL)
              .concat(Array(5).fill(''))
              .slice(0, 5)
          : Array(5).fill(''),
        alteracoesECG: Array.isArray(ergo.altECG)
          ? ergo.altECG
              .map(i => i.CODSEGST)
              .concat(Array(5).fill(''))
              .slice(0, 5)
          : Array(5).fill(''),
        arritmias: Array.isArray(ergo.arritmias)
          ? ergo.arritmias
              .map(i => i.CODARRITMIA)
              .concat(Array(5).fill(''))
              .slice(0, 5)
          : Array(5).fill(''),
        comportamentoPA: Array.isArray(ergo.comportamentoPA)
          ? ergo.comportamentoPA
              .map(i => i.CODCOMPORPA)
              .concat(Array(5).fill(''))
              .slice(0, 5)
          : Array(5).fill(''),
        comentario: Array.isArray(ergo.comentarios)
          ? ergo.comentarios
              .map(i => i.CODCOMENTARIO)
              .concat(Array(5).fill(''))
              .slice(0, 5)
          : Array(5).fill(''),
        conclusao: Array.isArray(ergo.conclusoes)
          ? ergo.conclusoes
              .map(i => i.CODCONCLU)
              .concat(Array(5).fill(''))
              .slice(0, 5)
          : Array(5).fill(''),

        estagios: Array.isArray(ergo.estagios)
          ? ergo.estagios
              .map((i, idx) => ({
                tempo: i.TEMPO || '',
                vel: i.VELOCIDADE || '',
                inc: i.INCLINACAO || '',
                fc: i.FC || '',
                pa: i.PA || '',
                sintomas: i.CODSAS || '',
                arritmias: i.CODSAS1 || '',
                id: i.CODESTAGIO || idx + 1,
              }))
              .concat(
                Array.from(
                  { length: Math.max(0, maxEstagios - ergo.estagios.length) },
                  (_, idx) => ({
                    tempo: '',
                    vel: '',
                    inc: '',
                    fc: '',
                    pa: '',
                    sintomas: '',
                    arritmias: '',
                    id: ergo.estagios.length + idx + 1,
                  })
                )
              )
              .slice(0, Math.max(maxEstagios, ergo.estagios.length))
          : Array.from({ length: maxEstagios }, (_, idx) => ({
              tempo: '',
              vel: '',
              inc: '',
              fc: '',
              pa: '',
              sintomas: '',
              arritmias: '',
              id: idx + 1,
            })),

        recuperacao: Array.isArray(ergo.recuperacoes)
          ? ergo.recuperacoes
              .map((i, idx) => ({
                tempo: i.TEMPO || '2',
                fc: i.FC || '',
                pa: i.PA || '',
                sintomas: i.CODSAS || '',
                arritmias: i.CODSAS1 || '',
                id: i.CODRECUPERACAO || idx + 1,
              }))
              .concat(
                Array.from(
                  { length: 4 - ergo.recuperacoes.length },
                  (_, idx) => ({
                    tempo: '2',
                    fc: '',
                    pa: '',
                    sintomas: '',
                    arritmias: '',
                    id: ergo.recuperacoes.length + idx + 1,
                  })
                )
              )
              .slice(0, 4)
          : Array.from({ length: 4 }, (_, idx) => ({
              tempo: '2',
              fc: '',
              pa: '',
              sintomas: '',
              arritmias: '',
              id: idx + 1,
            })),
      }));
    }
  }, [ergo]);

  const spanStyle = {
    display: 'block',
    fontSize: '0.8rem',
    backgroundColor: '#e0f2fe', // azul claro
    border: '1px solid #60a5fa', // azul médio
    borderRadius: 5,
    color: '#1e3a8a', // azul escuro
    fontWeight: 500,
    fontFamily: 'Segoe UI, sans-serif',
    marginBottom: 2,
    padding: '2px 6px',
  };

  const fieldKeys = [
    'indicacoes',
    'medicacoes',
    'fatoresRisco',
    'queixas',
    'atividadeFisica',
    'ecgBasal',
    'alteracoesECG',
    'arritmias',
    'comportamentoPA',
    'comentario',
    'conclusao',
  ];
  const cardioFields = [
    'fcMax',
    'fcSub',
    'fcDeitado',
    'paDeitado',
    'fcEmPe',
    'paEmPe',
  ];
  const fieldCount = 5; // Ex: 5 campos por grupo
  const numRecRows = formData.recuperacao.length;
  const numRecCols = 5; // tempo, fc, pa, sintomas, arritmias
  const numEstCols = 8; // Total de colunas da tabela de estágios
  const numEstRows = formData.estagios.length;
  const inputRefs = useRef({});
  const cardioRefs = useRef(cardioFields.map(() => React.createRef()));
  const createRefMatrix = (rows, cols) =>
    Array.from({ length: rows }, () =>
      Array.from({ length: cols }, () => React.createRef())
    );
  const recRefs = useRef(createRefMatrix(4, 5));
  const estRefs = useRef(createRefMatrix(12, 8));

  useEffect(() => {
    fieldKeys.forEach(key => {
      if (key === 'medicacoes') {
        inputRefs.current[key] = Array(10)
          .fill(null)
          .map(() => React.createRef());
      } else {
        inputRefs.current[key] = Array(fieldCount)
          .fill(null)
          .map(() => React.createRef());
      }
    });
  }, []);

  const getDefaultEstagios = protocolo => {
    let estagios = [];
    switch (protocolo) {
      case 'Rampa':
        estagios = Array.from({ length: 12 }, () => ({
          tempo: "1'",
          vel: '',
          inc: '0',
        }));
        break;
      case 'Bruce':
        estagios = [
          { tempo: "3'", vel: 1.7, inc: 10 },
          { tempo: "3'", vel: 2.5, inc: 12 },
          { tempo: "3'", vel: 3.4, inc: 14 },
          { tempo: "3'", vel: 4.2, inc: 16 },
          { tempo: "3'", vel: 5.0, inc: 18 },
          { tempo: "3'", vel: 5.5, inc: 20 },
          { tempo: "3'", vel: 6.0, inc: 22 },
        ];
        break;
      case 'Bruce Modificado':
        estagios = [
          { tempo: "3'", vel: 1.7, inc: 0 },
          { tempo: "3'", vel: 1.7, inc: 10 },
          { tempo: "3'", vel: 2.5, inc: 12 },
          { tempo: "3'", vel: 3.4, inc: 14 },
          { tempo: "3'", vel: 4.2, inc: 16 },
          { tempo: "3'", vel: 5.0, inc: 18 },
          { tempo: "3'", vel: 5.5, inc: 20 },
          { tempo: "3'", vel: 6.0, inc: 22 },
        ];
        break;
      case 'Ellestad':
        estagios = [
          { tempo: "3'", vel: 1.7, inc: 10 },
          { tempo: "2'", vel: 3.0, inc: 10 },
          { tempo: "2'", vel: 4.0, inc: 10 },
          { tempo: "3'", vel: 5.0, inc: 10 },
          { tempo: "2'", vel: 6.0, inc: 15 },
          { tempo: "2'", vel: 7.0, inc: 15 },
          { tempo: "2'", vel: 8.0, inc: 15 },
        ];
        break;
      default:
        estagios = Array.from({ length: 8 }, () => ({
          tempo: '',
          vel: '',
          inc: '',
        }));
    }
    // Preenche os campos restantes e id sequencial
    const size = protocolo === 'Rampa' ? 12 : 8;
    return Array.from({ length: size }, (_, idx) => ({
      tempo: estagios[idx]?.tempo || '',
      vel: estagios[idx]?.vel || '',
      inc: estagios[idx]?.inc || '0',
      fc: '',
      pa: '',
      sintomas: '',
      arritmias: '',
      id: idx + 1,
    }));
  };

  const handleTableKeyDown = (e, row, col, numRows, numCols, refs) => {
    const isRampa = numRows !== 8;
    const defaultColOnDown = 3;

    let newRow = row;
    let newCol = col;

    switch (e.key) {
      case 'ArrowRight':
        newCol = Math.min(col + 1, numCols - 1);
        break;

      case 'ArrowLeft':
        newCol = Math.max(col - 1, 0);
        break;

      case 'ArrowDown':
        newRow = Math.min(row + 1, numRows - 1);
        newCol = isRampa && col === 1 ? col : defaultColOnDown;
        break;

      case 'ArrowUp':
        newRow = Math.max(row - 1, 0);
        break;

      case 'Enter':
        e.preventDefault();
        if (refs === estRefs) {
          recRefs.current?.[0]?.[1]?.current?.focus();
          return;
        }
        if (refs === recRefs) {
          inputRefs.current?.alteracoesECG?.[0]?.current?.focus();
          return;
        }

        newRow = Math.min(row + 1, numRows - 1);
        break;

      default:
        return;
    }

    e.preventDefault();
    refs.current?.[newRow]?.[newCol]?.current?.focus();
  };

  const handleEstKeyDown = (e, row, col) =>
    handleTableKeyDown(e, row, col, numEstRows, numEstCols, estRefs);
  const handleRecKeyDown = (e, row, col) =>
    handleTableKeyDown(e, row, col, numRecRows, numRecCols, recRefs);

  const handleCardioKeyDown = (e, index) => {
    if (e.key === 'ArrowRight' && index < cardioRefs.current.length - 1) {
      cardioRefs.current[index + 1].current?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      cardioRefs.current[index - 1].current?.focus();
    } else if (e.key === 'Enter' && index < cardioRefs.current.length - 1) {
      cardioRefs.current[index + 1].current?.focus();
      e.preventDefault();
    }
  };

  const handleKeyDown = (e, group, index) => {
    const groupLength = formData[group]?.length || 0;
    if (e.key === 'ArrowRight' && index < groupLength - 1) {
      inputRefs.current[group][index + 1]?.current?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[group][index - 1]?.current?.focus();
    } else if (e.key === 'Enter') {
      const currentGroupIndex = fieldKeys.indexOf(group);
      const nextGroup = fieldKeys[currentGroupIndex + 1];
      if (nextGroup) {
        inputRefs.current[nextGroup][0]?.current?.focus();
      }
    }
  };

  const handleArrayChange = (e, field, index) => {
    const { value } = e.target;
    if (value === '' || /^\d+$/.test(value)) {
      const newArray = [...formData[field]];
      newArray[index] = value;
      setFormData(prev => ({ ...prev, [field]: newArray }));
    } else {
      toast.info('Digite apenas números neste campo.');
    }
  };

  const handleChangeProtocolo = e => {
    const { value } = e.target;
    setFormData(prev => ({
      ...prev,
      protocolo: value,
      estagios: getDefaultEstagios(value),
    }));
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleArrayObjectChange = (arrayName, index, field, value) => {
    setFormData(prev => {
      const updated = [...prev[arrayName]];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, [arrayName]: updated };
    });
  };

  const handleClearArrayAndDelete = async tipo => {
    const codExame = formData.codErgo;
    const ids = formData[tipo].filter(id => !!id);

    if (!codExame || ids.length === 0) {
      setFormData(prev => ({
        ...prev,
        [tipo]: Array(prev[tipo].length).fill(''),
      }));
      return;
    }

    try {
      let res;
      for (const id of ids) {
        const dataToDelete = { CODEXAME: codExame, ID: id, TIPO: tipo };
        res = await api.delete('/ergo-delete', { data: dataToDelete });
      }
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response.data.error);
    }

    setFormData(prev => ({
      ...prev,
      [tipo]: Array(prev[tipo].length).fill(''),
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Remove estágios onde fc está vazio, nulo ou zero
      const estagiosFiltrados = formData.estagios.filter(
        estagio =>
          estagio.fc !== '' && estagio.fc !== null && Number(estagio.fc) !== 0
      );

      // Remove recuperacao onde fc está vazio, nulo ou zero
      const recuperacaoFiltrada = formData.recuperacao.filter(
        rec => rec.fc !== ''
      );

      await api.post('/ergo-main', {
        codErgo: formData.codErgo,
        fcMax: formData.fcMax,
        fcSub: formData.fcSub,
        fcDeitado: formData.fcDeitado,
        fcEmPe: formData.fcEmPe,
        paDeitado: formData.paDeitado,
        paEmPe: formData.paEmPe,
        exameFisico: formData.exameFisico,
        protocolo: formData.protocolo,
      });

      // atualização sequencial por endpoint
      if (formData.indicacoes.some(val => val)) {
        await api.post('/ergo-indicacoes', {
          codErgo: formData.codErgo,
          indicacoes: formData.indicacoes.filter(val => val),
        });
      }

      if (formData.medicacoes.some(val => val)) {
        await api.post('/ergo-medicacoes', {
          codErgo: formData.codErgo,
          medicacoes: formData.medicacoes.filter(val => val),
        });
      }

      if (formData.queixas.some(val => val)) {
        await api.post('/ergo-queixas', {
          codErgo: formData.codErgo,
          queixas: formData.queixas.filter(val => val),
        });
      }

      if (formData.fatoresRisco.some(val => val)) {
        await api.post('/ergo-fatores-risco', {
          codErgo: formData.codErgo,
          fatoresRisco: formData.fatoresRisco.filter(val => val),
        });
      }

      if (formData.atividadeFisica.some(val => val)) {
        await api.post('/ergo-atividade-fisica', {
          codErgo: formData.codErgo,
          atividadeFisica: formData.atividadeFisica.filter(val => val),
        });
      }

      if (formData.ecgBasal.some(val => val)) {
        await api.post('/ergo-ecg-basal', {
          codErgo: formData.codErgo,
          ecgBasal: formData.ecgBasal.filter(val => val),
        });
      }

      if (formData.alteracoesECG.some(val => val)) {
        await api.post('/ergo-alteracoes-ecg', {
          codErgo: formData.codErgo,
          alteracoesECG: formData.alteracoesECG.filter(val => val),
        });
      }

      if (formData.arritmias.some(val => val)) {
        await api.post('/ergo-arritmias', {
          codErgo: formData.codErgo,
          arritmias: formData.arritmias.filter(val => val),
        });
      }

      if (formData.comportamentoPA.some(val => val)) {
        await api.post('/ergo-comportamento-pa', {
          codErgo: formData.codErgo,
          comportamentoPA: formData.comportamentoPA.filter(val => val),
        });
      }

      if (formData.comentario.some(val => val)) {
        await api.post('/ergo-comentario', {
          codErgo: formData.codErgo,
          comentario: formData.comentario.filter(val => val),
        });
      }

      if (formData.conclusao.some(val => val)) {
        await api.post('/ergo-conclusao', {
          codErgo: formData.codErgo,
          conclusao: formData.conclusao.filter(val => val),
        });
      }

      if (recuperacaoFiltrada.length > 0) {
        await api.post('/ergo-recuperacao', {
          codErgo: formData.codErgo,
          recuperacao: recuperacaoFiltrada,
        });
      }

      if (estagiosFiltrados.length > 0) {
        await api.post('/ergo-estagios', {
          codErgo: formData.codErgo,
          estagios: estagiosFiltrados,
        });
      }

      toast.success('Dados salvos com sucesso!');
      mutate();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao salvar dados');
    } finally {
      setIsSubmitting(false);
      window.scrollTo(0, 0);
    }
  };

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '80vh',
        }}
      >
        <Spinner animation="border" variant="dark" size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" style={{ marginTop: 10 }}>
        <Alert.Heading>Erro ao carregar laudo!</Alert.Heading>
        <p>Tente novamente.</p>
      </Alert>
    );
  }

  return (
    <Card className="mt-2 p-2">
      <LaudoCardHeader title={`Teste Ergométrico - ${ergo.CODEXAME}`} />
      <Card.Body>
        <Row className="mb-2">
          <Col>
            <Form.Label>Nome</Form.Label>
            <Form.Control
              name="nome"
              placeholder="Nome do Paciente"
              value={ergo.NOMEPAC}
              readOnly
            />
          </Col>
          <Col>
            <Form.Label>Data de Nascimento</Form.Label>
            <Form.Control
              name="dataNascimento"
              placeholder="Data de Nascimento"
              value={ergo.dataNasc}
              readOnly
            />
          </Col>
        </Row>
        <h4>I - Dados Clínicos</h4>
        <Row className="align-items-end mb-3">
          <Col xs="auto">
            <Form.Label>Indicações</Form.Label>
            <div className="d-flex flex-wrap gap-1">
              {formData.indicacoes.map((val, i) => (
                <Form.Control
                  key={i}
                  ref={inputRefs.current.indicacoes?.[i]}
                  size="sm"
                  style={{ width: '50px' }}
                  value={val}
                  onChange={e => handleArrayChange(e, 'indicacoes', i)}
                  onKeyDown={e => handleKeyDown(e, 'indicacoes', i)}
                />
              ))}
              <DeleteButton
                field="indicacoes"
                onDelete={handleClearArrayAndDelete}
              />
            </div>
            {ergo?.indicacoes?.map(o => (
              <span key={o.INDICACAO} style={spanStyle}>
                [ {o.CODINDICACAO} — {o.DESCRICAO || 'Descrição não encontrada'}{' '}
                ]
              </span>
            ))}
          </Col>

          <Col xs="auto">
            <Form.Label>Medicações</Form.Label>
            <div className="d-flex flex-wrap gap-1">
              {formData.medicacoes.map((val, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center' }}>
                  <Form.Control
                    size="sm"
                    style={{ width: '50px' }}
                    value={val}
                    ref={inputRefs.current.medicacoes?.[i]}
                    onChange={e => handleArrayChange(e, 'medicacoes', i)}
                    onKeyDown={e => handleKeyDown(e, 'medicacoes', i)}
                  />
                </div>
              ))}
              <DeleteButton
                field="medicacoes"
                onDelete={handleClearArrayAndDelete}
              />
            </div>
            {ergo?.medicamentos?.map(o => (
              <span key={o.CODMEDICTO} style={spanStyle}>
                [ {o.CODMEDICTO} — {o.FRASE || 'Descrição não encontrada'} ]
              </span>
            ))}
          </Col>

          <Col xs="auto">
            <Form.Label>Fatores de Risco</Form.Label>
            <div className="d-flex flex-wrap gap-1">
              {formData.fatoresRisco.map((val, i) => (
                <Form.Control
                  key={i}
                  ref={inputRefs.current.fatoresRisco?.[i]}
                  size="sm"
                  style={{ width: '50px' }}
                  value={val}
                  onChange={e => handleArrayChange(e, 'fatoresRisco', i)}
                  onKeyDown={e => handleKeyDown(e, 'fatoresRisco', i)}
                />
              ))}
              <DeleteButton
                field="fatoresRisco"
                onDelete={handleClearArrayAndDelete}
              />
            </div>
            {ergo?.fatoresRisco?.map(o => (
              <span key={o.CODFATRISCO} style={spanStyle}>
                [ {o.CODFATRISCO} — {o.DESCRICAO || 'Descrição não encontrada'}{' '}
                ]
              </span>
            ))}
          </Col>
          <Col xs="auto">
            <Form.Label>Queixas</Form.Label>
            <div className="d-flex flex-wrap gap-1">
              {formData.queixas.map((val, i) => (
                <Form.Control
                  key={i}
                  ref={inputRefs.current.queixas?.[i]}
                  size="sm"
                  style={{ width: '50px' }}
                  value={val}
                  onChange={e => handleArrayChange(e, 'queixas', i)}
                  onKeyDown={e => handleKeyDown(e, 'queixas', i)}
                />
              ))}
              <DeleteButton
                field="queixas"
                onDelete={handleClearArrayAndDelete}
              />
            </div>
            {ergo?.queixas?.map(o => (
              <span key={o.CODQUEIXA} style={spanStyle}>
                [ {o.CODQUEIXA} — {o.FRASE || 'Descrição não encontrada'} ]
              </span>
            ))}
          </Col>
        </Row>
        <h4>II - Avaliações</h4>
        <Row className="align-items-end mb-3">
          <Col xs="auto">
            <Form.Label>Ativ. Física</Form.Label>
            <div className="d-flex flex-wrap gap-1">
              {formData.atividadeFisica.map((val, i) => (
                <Form.Control
                  key={i}
                  size="sm"
                  style={{ width: '50px' }}
                  value={val}
                  onChange={e => handleArrayChange(e, 'atividadeFisica', i)}
                  ref={inputRefs.current.atividadeFisica?.[i]}
                  onKeyDown={e => handleKeyDown(e, 'atividadeFisica', i)}
                />
              ))}
              <DeleteButton
                field="atividadeFisica"
                onDelete={handleClearArrayAndDelete}
              />
            </div>
            {ergo?.atividadesFisica?.map(o => (
              <span key={o.CODATVFISICA} style={spanStyle}>
                [ {o.CODATVFISICA} — {o.DESCRICAO || 'Descrição não encontrada'}{' '}
                ]
              </span>
            ))}
          </Col>

          <Col xs="auto">
            <Form.Label>ECG Basal</Form.Label>
            <div className="d-flex flex-wrap gap-1">
              {formData.ecgBasal.map((val, i) => (
                <Form.Control
                  key={i}
                  size="sm"
                  style={{ width: '50px' }}
                  value={val}
                  onChange={e => handleArrayChange(e, 'ecgBasal', i)}
                  ref={inputRefs.current.ecgBasal?.[i]}
                  onKeyDown={e => handleKeyDown(e, 'ecgBasal', i)}
                />
              ))}
              <DeleteButton
                field="ecgBasal"
                onDelete={handleClearArrayAndDelete}
              />
            </div>
            {ergo?.ecgBasal?.map(o => (
              <span key={o.CODECGBASAL} style={spanStyle}>
                [ {o.CODECGBASAL} — {o.FRASE || 'Descrição não encontrada'} ]
              </span>
            ))}
          </Col>
        </Row>
        <Form.Label>Exame Físico</Form.Label>
        <InputGroup className="my-2">
          <Button
            variant="outline-secondary"
            onClick={() =>
              setFormData(prev => ({
                ...prev,
                exameFisico: 'RCR,  2T S/ SOPRO.  MV (+),  BILATERAL S/ RA.',
              }))
            }
            title="Preencher com padrão"
          >
            Frase Padrão
          </Button>
          <Form.Control
            name="exameFisico"
            placeholder="Exame Físico"
            onChange={handleChange}
            value={formData.exameFisico}
          />
        </InputGroup>
        <h4>III - Frequência Cardíaca Prevista</h4>
        <Row>
          <Col>
            <Form.Control
              name="fcMax"
              placeholder="FC Máx"
              onChange={handleChange}
              ref={cardioRefs.current[0]}
              onKeyDown={e => handleCardioKeyDown(e, 0)}
              value={formData.fcMax}
            />
          </Col>
          <Col>
            <Form.Control
              name="fcSub"
              placeholder="FC Sub"
              onChange={handleChange}
              ref={cardioRefs.current[1]}
              onKeyDown={e => handleCardioKeyDown(e, 1)}
              value={formData.fcSub}
            />
          </Col>
        </Row>
        <Row className="my-2">
          <Col>
            <Form.Control
              name="fcDeitado"
              placeholder="FC Deitado"
              onChange={handleChange}
              ref={cardioRefs.current[2]}
              onKeyDown={e => handleCardioKeyDown(e, 2)}
              value={formData.fcDeitado}
            />
          </Col>
          <Col>
            <InputMask
              mask="999/999"
              value={formData.paDeitado}
              onChange={e => {
                const val = e.target.value.replace(/[^0-9/]/g, '');
                const formattedValue = val.replace(/(\d{3})(\d{3})/, '$1/$2');
                handleChange({
                  target: { name: 'paDeitado', value: formattedValue },
                });
              }}
              onKeyDown={e => handleCardioKeyDown(e, 3)}
            >
              {inputProps => (
                <Form.Control
                  {...inputProps}
                  name="paDeitado"
                  placeholder="PA Deitado"
                  ref={cardioRefs.current[3]}
                />
              )}
            </InputMask>
          </Col>
        </Row>
        <Row>
          <Col>
            <Form.Control
              name="fcEmPe"
              placeholder="FC Em Pé"
              onChange={handleChange}
              ref={cardioRefs.current[4]}
              onKeyDown={e => handleCardioKeyDown(e, 4)}
              value={formData.fcEmPe}
            />
          </Col>
          <Col>
            <InputMask
              mask="999/999"
              value={formData.paEmPe}
              onChange={e => {
                const val = e.target.value.replace(/[^0-9/]/g, '');
                const formattedValue = val.replace(/(\d{3})(\d{3})/, '$1/$2');
                handleChange({
                  target: { name: 'paEmPe', value: formattedValue },
                });
              }}
              onKeyDown={e => handleCardioKeyDown(e, 5)}
            >
              {inputProps => (
                <Form.Control
                  {...inputProps}
                  name="paEmPe"
                  placeholder="PA Em Pé"
                  ref={cardioRefs.current[5]}
                />
              )}
            </InputMask>
          </Col>
        </Row>
        <Form.Group className="my-2">
          <h4>IV - Protocolo</h4>
          <Form.Label>
            <strong>Protocolo</strong>
          </Form.Label>
          <div>
            <Form.Check
              inline
              type="radio"
              label="Ellestad"
              name="protocolo"
              value="Ellestad"
              checked={formData.protocolo === 'Ellestad'}
              onChange={handleChangeProtocolo}
            />
            <Form.Check
              inline
              type="radio"
              label="Bruce"
              name="protocolo"
              value="Bruce"
              checked={formData.protocolo === 'Bruce'}
              onChange={handleChangeProtocolo}
            />
            <Form.Check
              inline
              type="radio"
              label="Bruce Modificado"
              name="protocolo"
              value="Bruce Modificado"
              checked={formData.protocolo === 'Bruce Modificado'}
              onChange={handleChangeProtocolo}
            />
            {/* Opção Rampa Adicionada */}
            <Form.Check
              inline
              type="radio"
              label="Rampa"
              name="protocolo"
              value="Rampa"
              checked={formData.protocolo === 'Rampa'}
              onChange={handleChangeProtocolo}
            />
          </div>
        </Form.Group>
        <h5>Estágios</h5>
        <Table striped bordered size="sm">
          <thead>
            <tr>
              <th>Estágio</th>
              <th>Tempo</th>
              <th>Vel</th>
              <th>Inc</th>
              <th>FC</th>
              <th>PA</th>
              <th>Sinais/Sintomas</th>
              <th>Arritmias</th>
            </tr>
          </thead>
          <tbody>
            {formData.estagios.map((estagio, i) => (
              <tr key={estagio.id || i}>
                <td>{estagio.id}</td>
                <td>
                  <Form.Control
                    size="sm"
                    value={estagio.tempo}
                    ref={estRefs.current[i]?.[0]}
                    onChange={e =>
                      handleArrayObjectChange(
                        'estagios',
                        i,
                        'tempo',
                        e.target.value
                      )
                    }
                    onKeyDown={e => handleEstKeyDown(e, i, 0)}
                  />
                </td>
                <td>
                  <Form.Control
                    size="sm"
                    value={estagio.vel}
                    ref={estRefs.current[i]?.[1]}
                    onChange={e =>
                      handleArrayObjectChange(
                        'estagios',
                        i,
                        'vel',
                        e.target.value
                      )
                    }
                    onKeyDown={e => handleEstKeyDown(e, i, 1)}
                  />
                </td>
                <td>
                  <Form.Control
                    size="sm"
                    value={estagio.inc}
                    ref={estRefs.current[i]?.[2]}
                    onChange={e =>
                      handleArrayObjectChange(
                        'estagios',
                        i,
                        'inc',
                        e.target.value
                      )
                    }
                    onKeyDown={e => handleEstKeyDown(e, i, 2)}
                  />
                </td>
                <td>
                  <Form.Control
                    size="sm"
                    value={estagio.fc}
                    ref={estRefs.current[i]?.[3]}
                    onChange={e =>
                      handleArrayObjectChange(
                        'estagios',
                        i,
                        'fc',
                        e.target.value
                      )
                    }
                    onKeyDown={e => handleEstKeyDown(e, i, 3)}
                  />
                </td>
                <td>
                  <InputMask
                    mask="999/999"
                    value={estagio.pa}
                    onChange={e => {
                      const val = e.target.value.replace(/[^0-9/]/g, '');
                      const formattedValue = val.replace(
                        /(\d{3})(\d{3})/,
                        '$1/$2'
                      );
                      handleArrayObjectChange(
                        'estagios',
                        i,
                        'pa',
                        formattedValue
                      );
                    }}
                    onKeyDown={e => handleEstKeyDown(e, i, 4)}
                  >
                    {inputProps => (
                      <Form.Control
                        {...inputProps}
                        size="sm"
                        ref={estRefs.current[i]?.[4]}
                      />
                    )}
                  </InputMask>
                </td>
                <td>
                  <Form.Control
                    size="sm"
                    value={estagio.sintomas}
                    ref={estRefs.current[i]?.[5]}
                    onChange={e =>
                      handleArrayObjectChange(
                        'estagios',
                        i,
                        'sintomas',
                        e.target.value
                      )
                    }
                    onKeyDown={e => handleEstKeyDown(e, i, 5)}
                  />
                </td>
                <td>
                  <Form.Control
                    size="sm"
                    value={estagio.arritmias}
                    ref={estRefs.current[i]?.[6]}
                    onChange={e =>
                      handleArrayObjectChange(
                        'estagios',
                        i,
                        'arritmias',
                        e.target.value
                      )
                    }
                    onKeyDown={e => handleEstKeyDown(e, i, 6)}
                  />
                </td>
                <td>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    style={{ marginLeft: 4, padding: '0 6px', height: 30 }}
                    onClick={async () => {
                      const codExame = formData.codErgo;
                      const est = formData.estagios[i];

                      if (!codExame) return;

                      const dataToDelete = {
                        CODEXAME: codExame,
                        ID: est.id,
                        TIPO: 'estagios',
                      };

                      try {
                        await api.delete('/ergo-delete', {
                          data: dataToDelete,
                        });
                        toast.success('Estágio excluído com sucesso!');
                        // Limpa apenas a linha excluída no frontend
                        setFormData(prev => {
                          const novosEstagios = [...prev.estagios];
                          novosEstagios[i] = {
                            tempo: '',
                            vel: '',
                            inc: '',
                            fc: '',
                            pa: '',
                            sintomas: '',
                            arritmias: '',
                            id: '',
                          };
                          return { ...prev, estagios: novosEstagios };
                        });
                      } catch {
                        toast.error('Erro ao excluir estágio!');
                      }
                    }}
                    tabIndex={-1}
                  >
                    ×
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        {ergo?.estagios?.map(o => (
          <span key={o.CODESTAGIO} style={spanStyle}>
            [ {o.CODESTAGIO} — Sinais/Sintomas: {o.Sintomas || ' - '} |
            Arritmias: {o.Arritmias || ' - '} ]
          </span>
        ))}
        <h5>Recuperação</h5>
        <Table striped bordered size="sm">
          <thead>
            <tr>
              <th>Estágio</th>
              <th>Tempo</th>
              <th>FC</th>
              <th>PA</th>
              <th>Sinais/Sintomas</th>
              <th>Arritmias</th>
            </tr>
          </thead>
          <tbody>
            {formData.recuperacao.map((_, i) => (
              <tr key={i}>
                <td>{i + 1}</td>
                <td>
                  <Form.Control
                    size="sm"
                    value={formData.recuperacao[i].tempo}
                    ref={recRefs.current[i][0]}
                    onKeyDown={e => handleRecKeyDown(e, i, 0)}
                    onChange={e =>
                      handleArrayObjectChange(
                        'recuperacao',
                        i,
                        'tempo',
                        e.target.value
                      )
                    }
                  />
                </td>
                <td>
                  <Form.Control
                    size="sm"
                    value={formData.recuperacao[i].fc}
                    ref={recRefs.current[i][1]}
                    onKeyDown={e => handleRecKeyDown(e, i, 1)}
                    onChange={e =>
                      handleArrayObjectChange(
                        'recuperacao',
                        i,
                        'fc',
                        e.target.value
                      )
                    }
                  />
                </td>
                <td>
                  <InputMask
                    mask="999/999"
                    value={formData.recuperacao[i].pa}
                    onChange={e => {
                      const val = e.target.value.replace(/[^0-9/]/g, '');
                      const formattedValue = val.replace(
                        /(\d{3})(\d{3})/,
                        '$1/$2'
                      );
                      handleArrayObjectChange(
                        'recuperacao',
                        i,
                        'pa',
                        formattedValue
                      );
                    }}
                    onKeyDown={e => handleRecKeyDown(e, i, 2)}
                  >
                    {inputProps => (
                      <Form.Control
                        {...inputProps}
                        size="sm"
                        ref={recRefs.current[i][2]}
                      />
                    )}
                  </InputMask>
                </td>
                <td>
                  <Form.Control
                    size="sm"
                    value={formData.recuperacao[i].sintomas}
                    ref={recRefs.current[i][3]}
                    onKeyDown={e => handleRecKeyDown(e, i, 3)}
                    onChange={e =>
                      handleArrayObjectChange(
                        'recuperacao',
                        i,
                        'sintomas',
                        e.target.value
                      )
                    }
                  />
                </td>
                <td>
                  <Form.Control
                    size="sm"
                    value={formData.recuperacao[i].arritmias}
                    ref={recRefs.current[i][4]}
                    onKeyDown={e => handleRecKeyDown(e, i, 4)}
                    onChange={e =>
                      handleArrayObjectChange(
                        'recuperacao',
                        i,
                        'arritmias',
                        e.target.value
                      )
                    }
                  />
                </td>
                <td>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    style={{ marginLeft: 4, padding: '0 6px', height: 30 }}
                    onClick={async () => {
                      const codExame = formData.codErgo;
                      const rec = formData.recuperacao[i]; // i = índice da linha
                      if (!codExame) return;

                      const dataToDelete = {
                        CODEXAME: codExame,
                        ID: rec.id,
                        TIPO: 'recuperacao',
                      };

                      try {
                        await api.delete('/ergo-delete', {
                          data: dataToDelete,
                        });
                        toast.success('Recuperação excluída com sucesso!');
                        // Limpa apenas a linha excluída no frontend
                        setFormData(prev => {
                          const novaRec = [...prev.recuperacao];
                          novaRec[i] = {
                            tempo: '',
                            fc: '',
                            pa: '',
                            sintomas: '',
                            arritmias: '',
                            id: '',
                          };
                          return { ...prev, recuperacao: novaRec };
                        });
                      } catch {
                        toast.error('Erro ao excluir recuperação!');
                      }
                    }}
                    tabIndex={-1}
                  >
                    ×
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        {ergo?.recuperacoes?.map(o => (
          <span key={o.CODRECUPERACAO} style={spanStyle}>
            [ {o.CODRECUPERACAO} — Sinais/Sintomas: {o.Sintomas || ' - '} |
            Arritmias: {o.Arritmias || ' - '} ]
          </span>
        ))}
        <h4>V - Alterações Eletrocardiográficas</h4>
        <Row>
          <Col xs="auto">
            <Form.Label>Alterações ECG</Form.Label>
            <div className="d-flex flex-wrap gap-1">
              {formData.alteracoesECG.map((val, i) => (
                <Form.Control
                  key={i}
                  size="sm"
                  style={{ width: '50px' }}
                  value={val}
                  onChange={e => handleArrayChange(e, 'alteracoesECG', i)}
                  ref={inputRefs.current.alteracoesECG?.[i]}
                  onKeyDown={e => handleKeyDown(e, 'alteracoesECG', i)}
                />
              ))}
              <DeleteButton
                field="alteracoesECG"
                onDelete={handleClearArrayAndDelete}
              />
            </div>
            {ergo?.altECG?.map(o => (
              <span key={o.CODSEGST} style={spanStyle}>
                [ {o.CODSEGST} — {o.DESCRICAO || 'Descrição não encontrada'} ]
              </span>
            ))}
          </Col>
        </Row>
        <h4>VI - Conclusões</h4>
        <Row>
          <Col xs="auto">
            <Form.Label>Arritmias</Form.Label>
            <div className="d-flex flex-wrap gap-1">
              {formData.arritmias.map((val, i) => (
                <Form.Control
                  key={i}
                  size="sm"
                  style={{ width: '50px' }}
                  value={val}
                  onChange={e => handleArrayChange(e, 'arritmias', i)}
                  ref={inputRefs.current.arritmias?.[i]}
                  onKeyDown={e => handleKeyDown(e, 'arritmias', i)}
                />
              ))}
              <DeleteButton
                field="arritmias"
                onDelete={handleClearArrayAndDelete}
              />
            </div>
            {ergo?.arritmias?.map(o => (
              <span key={o.CODARRITMIAS} style={spanStyle}>
                [ {o.CODARRITMIAS} — {o.DESCRICAO || 'Descrição não encontrada'}{' '}
                ]
              </span>
            ))}
          </Col>
          <Col xs="auto">
            <Form.Label>Comportamento PA</Form.Label>
            <div className="d-flex flex-wrap gap-1">
              {formData.comportamentoPA.map((val, i) => (
                <Form.Control
                  key={i}
                  size="sm"
                  style={{ width: '50px' }}
                  value={val}
                  onChange={e => handleArrayChange(e, 'comportamentoPA', i)}
                  ref={inputRefs.current.comportamentoPA?.[i]}
                  onKeyDown={e => handleKeyDown(e, 'comportamentoPA', i)}
                />
              ))}
              <DeleteButton
                field="comportamentoPA"
                onDelete={handleClearArrayAndDelete}
              />
            </div>
            {ergo?.comportamentoPA?.map(o => (
              <span key={o.CODCOMPORPA} style={spanStyle}>
                [ {o.CODCOMPORPA} — {o.DESCRICAO || 'Descrição não encontrada'}{' '}
                ]
              </span>
            ))}
          </Col>
        </Row>
        <Row>
          <Col xs="auto">
            <Form.Label>Comentários</Form.Label>
            <div className="d-flex flex-wrap gap-1">
              {formData.comentario.map((val, i) => (
                <Form.Control
                  key={i}
                  size="sm"
                  style={{ width: '50px' }}
                  value={val}
                  onChange={e => handleArrayChange(e, 'comentario', i)}
                  ref={inputRefs.current.comentario?.[i]}
                  onKeyDown={e => handleKeyDown(e, 'comentario', i)}
                />
              ))}
              <DeleteButton
                field="comentario"
                onDelete={handleClearArrayAndDelete}
              />
            </div>
            {ergo?.comentarios?.map(o => (
              <span key={o.CODCOMENTARIO} style={spanStyle}>
                [ {o.CODCOMENTARIO} —{' '}
                {o.DESCRICAO || 'Descrição não encontrada'} ]
              </span>
            ))}
          </Col>
          <Col xs="auto">
            <Form.Label>Conclusões</Form.Label>
            <div className="d-flex flex-wrap gap-1">
              {formData.conclusao.map((val, i) => (
                <Form.Control
                  key={i}
                  size="sm"
                  style={{ width: '50px' }}
                  value={val}
                  onChange={e => handleArrayChange(e, 'conclusao', i)}
                  ref={inputRefs.current.conclusao?.[i]}
                  onKeyDown={e => handleKeyDown(e, 'conclusao', i)}
                />
              ))}
              <DeleteButton
                field="conclusao"
                onDelete={handleClearArrayAndDelete}
              />
            </div>
            {ergo?.conclusoes?.map(o => (
              <span key={o.CODCONCLU} style={spanStyle}>
                [ {o.CODCONCLU} — {o.DESCRICAO || 'Descrição não encontrada'} ]
              </span>
            ))}
          </Col>
        </Row>

        <Card
          className="p-2 mt-3"
          style={{ fontSize: 12, backgroundColor: '#f8f9fa' }}
        >
          <strong>ONDE DESEJA A QUEBRA DE PÁGINA (PDF):</strong>
          <div className="d-flex flex-wrap align-items-center mt-1">
            <Form.Check
              inline
              label="Antes da Frequência Cardíaca Prevista"
              type="checkbox"
              id="formErgoBreakA"
              checked={pageBreakA}
              onChange={e => setPageBreakA(e.target.checked)}
            />
            <Form.Check
              inline
              label="Depois da Frequência Cardíaca Prevista"
              type="checkbox"
              id="formErgoBreakB"
              checked={pageBreakB}
              onChange={e => setPageBreakB(e.target.checked)}
            />
            <Form.Check
              inline
              label="Antes de Alterações Eletrocardiográficas"
              type="checkbox"
              id="formErgoBreakC"
              checked={pageBreakC}
              onChange={e => setPageBreakC(e.target.checked)}
            />
          </div>
        </Card>
      </Card.Body>

      <Card.Footer className="text-center">
        <Button onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
              />{' '}
              Salvando...
            </>
          ) : (
            'Gravar'
          )}
        </Button>{' '}
        <Button
          variant="secondary"
          disabled={isSubmitting}
          onClick={() => {
            history.push({
              pathname: `/reportErgo/${id}`,
              state: {
                printing: false,
                pageBreakA,
                pageBreakB,
                pageBreakC,
              },
            });
          }}
        >
          Imprimir
        </Button>{' '}
        <Button
          variant="secondary"
          disabled={isSubmitting}
          onClick={() => {
            history.push({
              pathname: `/reportErgo/${id}`,
              state: {
                printing: true,
                pageBreakA,
                pageBreakB,
                pageBreakC,
              },
            });
          }}
        >
          Imprimir Timbrado
        </Button>{' '}
        <Button
          variant="outline-success"
          disabled={isSubmitting}
          onClick={() =>
            generateErgoPDF(ergo, 'envioWpp', 0.9, {
              pageBreakA,
              pageBreakB,
              pageBreakC,
            })
          }
        >
          <FaWhatsapp /> Enviar PDF
        </Button>
      </Card.Footer>
    </Card>
  );
}
